/**
 * VoucherProgramPage · v2.7.65+E132 (2026-02-XX)
 * ═════════════════════════════════════════════════════════════════════
 * Buller Taxi Community Voucher Program — public application form.
 *
 * 3 sections per operator spec:
 *   1. Applicant Details + Identification (name, contact, dependents, ID upload)
 *   2. Eligibility & Assistance Verification (program checkboxes + doc upload)
 *   3. Program Acknowledgment & Submission
 *
 * Corporate palette: Yellows, Reds, Grays, Pinks.
 * Mobile-first responsive layout.
 * Uploads → /api/vouchers/upload-document (GridFS-backed).
 * Submit  → /api/vouchers/apply.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText, Download, Upload, Plus, Minus, CheckCircle2, Loader2,
  Phone, MapPin, Users, ClipboardCheck, ShieldCheck, X, ExternalLink,
  MessageCircle,
} from "lucide-react";
import SampleVoucherPreview from "@/components/marketing/SampleVoucherPreview";
import SmsConsent from "@/components/marketing/SmsConsent";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────── Constants ──────────────────────────── */
const ASSISTANCE_PROGRAMS = [
  "SNAP",
  "Medicaid/SoonerCare",
  "SSI/SSDI",
  "TANF",
  "Housing Assistance",
  "Other",
];

const ID_MAX_MB = 20;
const VERIF_MAX_MB = 10;
const ACCEPT_EXT = ".pdf,.png,.jpg,.jpeg";

const empty = {
  first_name: "", middle_name: "", last_name: "",
  phone: "", email: "",
  address: "", city: "", state: "OK", zip_code: "",
  assistance_programs: [],
  dependents: [],
  acknowledged: false,
};

function extOf(name = "") {
  if (!name.includes(".")) return "";
  return name.split(".").pop().toLowerCase();
}

/* ─────────────────────────── Upload widget ──────────────────────── */
function DocUploader({ kind, maxMB, uploaded, onUploaded, onClear, testid }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const handleFile = async (file) => {
    if (!file) return;
    if (!["pdf", "png", "jpg", "jpeg"].includes(extOf(file.name))) {
      toast.error("Only PDF, PNG, JPG, or JPEG files accepted");
      return;
    }
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`File exceeds ${maxMB}MB limit`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await axios.post(`${API}/api/vouchers/upload-document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded({ ...data, filename: data.filename || file.name });
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  if (uploaded) {
    return (
      <div
        data-testid={`${testid}-uploaded`}
        className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-3 py-2"
      >
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-emerald-100 truncate">{uploaded.filename}</div>
            <div className="text-xs text-emerald-300/70">
              {(uploaded.size / 1024).toFixed(0)} KB · {uploaded.doc_id.slice(-8)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          data-testid={`${testid}-clear`}
          className="text-rose-300 hover:text-rose-100"
          aria-label="Remove uploaded file"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <label
      data-testid={`${testid}-dropzone`}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 cursor-pointer transition ${
        dragOver
          ? "border-amber-400 bg-amber-500/10"
          : "border-amber-500/40 hover:border-amber-400 hover:bg-amber-500/5"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
      }}
    >
      {busy
        ? <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
        : <Upload className="h-8 w-8 text-amber-400" />}
      <div className="text-sm text-zinc-200 text-center">
        {busy ? "Uploading…" : "Click or drag file here"}
      </div>
      <div className="text-[11px] text-zinc-500 text-center">
        PDF, PNG, JPG, JPEG · Max {maxMB} MB
      </div>
      <input
        type="file"
        accept={ACCEPT_EXT}
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </label>
  );
}

/* ─────────────────────────── Section headers ─────────────────────── */
function SectionHeader({ n, title, Icon, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-4 pb-3 border-b-2 border-amber-500/40">
      <div className="flex items-center justify-center rounded-full bg-amber-500 text-black font-extrabold h-9 w-9 flex-shrink-0">
        {n}
      </div>
      <div>
        <h2 className="text-lg sm:text-xl font-extrabold text-amber-300 uppercase tracking-wide flex items-center gap-2">
          <Icon className="h-5 w-5" />{title}
        </h2>
        {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ─────────────────────────── Page ─────────────────────────────── */
export default function VoucherProgramPage() {
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState(empty);
  const [idDoc, setIdDoc] = useState(null);
  const [verifDoc, setVerifDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  // v2.7.65+E165 · TCPA-style SMS consent — recorded server-side with the
  // application. Optional (not required for form submit); disclosure block
  // rendered by <SmsConsent/> above the submit button.
  const [smsConsent, setSmsConsent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/vouchers/policy`).then((r) => setPolicy(r.data)).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProgram = (p) => setForm((f) => ({
    ...f,
    assistance_programs: f.assistance_programs.includes(p)
      ? f.assistance_programs.filter((x) => x !== p)
      : [...f.assistance_programs, p],
  }));

  const addDependent = () => setForm((f) => ({
    ...f,
    dependents: [...f.dependents, { first_name: "", middle_name: "", last_name: "", dob: "" }],
  }));
  const removeDependent = (idx) => setForm((f) => ({
    ...f,
    dependents: f.dependents.filter((_, i) => i !== idx),
  }));
  const setDependent = (idx, patch) => setForm((f) => ({
    ...f,
    dependents: f.dependents.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
  }));

  const canSubmit = useMemo(() => {
    if (!form.acknowledged || !idDoc || !verifDoc) return false;
    if (form.assistance_programs.length === 0) return false;
    for (const k of ["first_name", "last_name", "phone", "email",
                     "address", "city", "state", "zip_code"]) {
      if (!form[k] || String(form[k]).trim().length < 1) return false;
    }
    for (const d of form.dependents) {
      if (!d.first_name || !d.last_name || !d.dob) return false;
    }
    return true;
  }, [form, idDoc, verifDoc]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        state: (form.state || "").toUpperCase(),
        id_document_doc_id:            idDoc.doc_id,
        id_document_url:               idDoc.url,
        id_document_name:              idDoc.filename,
        verification_document_doc_id:  verifDoc.doc_id,
        verification_document_url:     verifDoc.url,
        verification_document_name:    verifDoc.filename,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
      };
      const { data } = await axios.post(`${API}/api/vouchers/apply`, payload);
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Application submit failed");
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setForm(empty);
    setIdDoc(null);
    setVerifDoc(null);
    setSubmitted(null);
  };

  /* ────────────── Post-submit thank-you screen ────────────── */
  if (submitted) {
    return (
      <MarketingLayout>
        <div className="min-h-[60vh] px-4 sm:px-6 py-12 flex items-center justify-center bg-zinc-950">
          <div
            className="max-w-lg w-full rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-500/10 to-rose-500/10 p-8 text-center shadow-2xl"
            data-testid="voucher-thankyou"
          >
            <div className="mx-auto rounded-full bg-emerald-500/20 border-2 border-emerald-400 h-16 w-16 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-extrabold text-amber-300 uppercase tracking-wider mb-2">
              Application Received
            </h1>
            <p className="text-zinc-200 mb-4">
              Thanks for applying to the Buller Taxi CCVAP Program (Community Care Voucher Assistance Program).
              A staff member will review your submission within{" "}
              <strong className="text-amber-300">{submitted.review_hours || 72} hours</strong>.
            </p>
            <div className="text-xs text-zinc-400 font-mono mb-6 border border-white/10 rounded p-2">
              Confirmation: {submitted.application_id}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                variant="outline"
                onClick={reset}
                data-testid="voucher-thankyou-new"
                className="border-amber-500 text-amber-200 hover:bg-amber-500/10"
              >
                Submit another application
              </Button>
              <a href="/" className="text-sm text-zinc-400 hover:text-amber-300 inline-flex items-center justify-center gap-1">
                Back to home →
              </a>
            </div>
          </div>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 sm:px-6 lg:px-10 py-8" data-testid="voucher-program-page">
        <div className="max-w-4xl mx-auto">
          {/* v2.7.65+E133 · Anti-fraud sample voucher preview.
              Rendered ABOVE the hero so a rider sees what a real Community
              Care voucher looks like before applying — but a bad actor
              cannot lift a clean digital copy to forge one. See the
              SampleVoucherPreview component for the layered defenses. */}
          <SampleVoucherPreview />
          {/* Hero */}
          <div className="rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-500/10 via-rose-500/10 to-transparent p-6 sm:p-8 mb-8 shadow-xl" data-testid="voucher-hero">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-amber-500 text-black p-3">
                  <ShieldCheck className="h-8 w-8" />
                </div>
                <div>
                  <h1
                    className="text-3xl sm:text-4xl font-black text-amber-300 uppercase tracking-tight leading-tight"
                    data-testid="voucher-hero-title"
                  >
                    CCVAP Program
                  </h1>
                  <p className="text-base sm:text-lg text-amber-200 font-bold mt-1" data-testid="voucher-hero-subtitle">
                    Community Care Voucher Assistance Program (CCVAP)
                  </p>
                  <p className="text-sm text-zinc-300 mt-1">
                    Buller Taxi Services, LLC · Weatherford, OK
                  </p>
                </div>
              </div>
              <a
                href="/docs/Community_Voucher_Program_Policy.pdf"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold px-4 py-2.5 transition"
                data-testid="voucher-policy-download"
              >
                <FileText className="h-5 w-5" />
                View Program PDF
                <Download className="h-4 w-4" />
              </a>
              {/* v2.7.65+E133 · Voucher Ledger deep-link */}
              <a
                href="/my-vouchers"
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold px-4 py-2.5 transition"
                data-testid="voucher-ledger-link"
              >
                <ClipboardCheck className="h-5 w-5" />
                Check My Vouchers
              </a>
            </div>
            {policy && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <div className="rounded-lg bg-black/40 border border-amber-500/30 p-3 text-center" data-testid="voucher-stat-value">
                  <div className="text-2xl font-black text-amber-300">${policy.voucher_value_usd?.toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Per Ride</div>
                </div>
                <div className="rounded-lg bg-black/40 border border-amber-500/30 p-3 text-center" data-testid="voucher-stat-monthly">
                  <div className="text-2xl font-black text-amber-300">{policy.monthly_limit_adult}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Per Adult / Month</div>
                </div>
                <div className="rounded-lg bg-black/40 border border-amber-500/30 p-3 text-center" data-testid="voucher-stat-child">
                  <div className="text-2xl font-black text-amber-300">{policy.monthly_limit_child}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Per Child / Month</div>
                </div>
                <div className="rounded-lg bg-black/40 border border-amber-500/30 p-3 text-center" data-testid="voucher-stat-advance">
                  <div className="text-2xl font-black text-amber-300">{policy.advance_booking_hours}h</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Advance Booking</div>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-zinc-300">
              <a
                href={`tel:${(policy?.dispatch_phone || "").replace(/\D/g, "")}`}
                className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200 font-bold"
                data-testid="voucher-phone"
              >
                <Phone className="h-4 w-4" />
                {policy?.dispatch_phone || "(580) 219-4687"}
              </a>
              <span className="text-zinc-500">·</span>
              <span>Physical embossed voucher required at pickup</span>
              <span className="text-zinc-500">·</span>
              <span>Chamber Bucks accepted</span>
            </div>
            {/* v2.7.65+E132 · Live-chat "type human" escalate hint so riders know
                they can reach a person from anywhere on the site. */}
            <div
              className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 flex items-start gap-2"
              data-testid="voucher-chat-hint"
            >
              <MessageCircle className="h-4 w-4 text-amber-300 flex-shrink-0 mt-0.5"/>
              <span>
                <span className="font-bold">Prefer to chat?</span> Message dispatch anytime through our website&apos;s live-chat window — just type <span className="font-black text-amber-300">&quot;human&quot;</span> to escalate to a real person.
              </span>
            </div>
          </div>

          {/* Application form */}
          <div className="space-y-8">
            {/* ─────────────── Section 1 ─────────────── */}
            <section className="rounded-2xl border border-amber-500/25 bg-zinc-900/80 p-5 sm:p-6" data-testid="voucher-section-1">
              <SectionHeader n={1} title="Applicant Details & Identification" Icon={Users}
                subtitle="Required · used to verify eligibility and issue named vouchers" />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">First Name *</label>
                  <Input value={form.first_name} onChange={(e) => setField("first_name", e.target.value)}
                         data-testid="voucher-first-name" className="bg-black/40 border-white/15 text-white" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Middle Name</label>
                  <Input value={form.middle_name} onChange={(e) => setField("middle_name", e.target.value)}
                         data-testid="voucher-middle-name" className="bg-black/40 border-white/15 text-white" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Last Name *</label>
                  <Input value={form.last_name} onChange={(e) => setField("last_name", e.target.value)}
                         data-testid="voucher-last-name" className="bg-black/40 border-white/15 text-white" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Phone Number *</label>
                  <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)}
                         placeholder="(580) 555-1234"
                         data-testid="voucher-phone-input" className="bg-black/40 border-white/15 text-white" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Email *</label>
                  <Input value={form.email} onChange={(e) => setField("email", e.target.value)}
                         type="email" placeholder="you@example.com"
                         data-testid="voucher-email" className="bg-black/40 border-white/15 text-white" />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs text-zinc-400 mb-1 block">Street Address *</label>
                <Input value={form.address} onChange={(e) => setField("address", e.target.value)}
                       data-testid="voucher-address" className="bg-black/40 border-white/15 text-white" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
                <div className="sm:col-span-2">
                  <label className="text-xs text-zinc-400 mb-1 block">City *</label>
                  <Input value={form.city} onChange={(e) => setField("city", e.target.value)}
                         data-testid="voucher-city" className="bg-black/40 border-white/15 text-white" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">State *</label>
                  <Input value={form.state} onChange={(e) => setField("state", e.target.value.toUpperCase())}
                         maxLength={2} data-testid="voucher-state" className="bg-black/40 border-white/15 text-white uppercase" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">ZIP *</label>
                  <Input value={form.zip_code} onChange={(e) => setField("zip_code", e.target.value)}
                         placeholder="73096"
                         data-testid="voucher-zip" className="bg-black/40 border-white/15 text-white" />
                </div>
              </div>

              {/* Dependent children */}
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 mb-6" data-testid="voucher-dependents">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-rose-300 uppercase tracking-wider">
                    Dependent Children (optional)
                  </h3>
                  <Button
                    type="button" size="sm"
                    onClick={addDependent}
                    data-testid="voucher-add-dependent"
                    className="bg-rose-600 hover:bg-rose-500 text-white font-bold"
                  >
                    <Plus className="h-4 w-4 mr-1" />Add Child
                  </Button>
                </div>
                {form.dependents.length === 0 && (
                  <p className="text-xs text-zinc-400" data-testid="voucher-dependents-empty">
                    Add each dependent child for a named child voucher. Child must be
                    physically present with the rider to use a child voucher.
                  </p>
                )}
                {form.dependents.map((dep, idx) => (
                  <div
                    key={idx}
                    data-testid={`voucher-dependent-${idx}`}
                    className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-2 pb-2 border-b border-white/5 last:border-0"
                  >
                    <Input placeholder="First name" value={dep.first_name}
                           onChange={(e) => setDependent(idx, { first_name: e.target.value })}
                           data-testid={`voucher-dep-first-${idx}`} className="bg-black/40 border-white/15 text-white text-sm" />
                    <Input placeholder="Middle" value={dep.middle_name}
                           onChange={(e) => setDependent(idx, { middle_name: e.target.value })}
                           data-testid={`voucher-dep-middle-${idx}`} className="bg-black/40 border-white/15 text-white text-sm" />
                    <Input placeholder="Last name" value={dep.last_name}
                           onChange={(e) => setDependent(idx, { last_name: e.target.value })}
                           data-testid={`voucher-dep-last-${idx}`} className="bg-black/40 border-white/15 text-white text-sm" />
                    <Input type="date" value={dep.dob}
                           onChange={(e) => setDependent(idx, { dob: e.target.value })}
                           data-testid={`voucher-dep-dob-${idx}`} className="bg-black/40 border-white/15 text-white text-sm" />
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => removeDependent(idx)}
                      data-testid={`voucher-dep-remove-${idx}`}
                      className="border-rose-500 text-rose-300 hover:bg-rose-500/10"
                    >
                      <Minus className="h-4 w-4 mr-1" />Remove
                    </Button>
                  </div>
                ))}
              </div>

              {/* ID upload */}
              <div>
                <label className="text-xs text-zinc-400 mb-2 block flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" />
                  ID Document (Government ID, Driver&apos;s License, Passport, or SSN Card) *
                </label>
                <DocUploader
                  kind="id" maxMB={ID_MAX_MB}
                  uploaded={idDoc}
                  onUploaded={setIdDoc}
                  onClear={() => setIdDoc(null)}
                  testid="voucher-id-upload"
                />
              </div>
            </section>

            {/* ─────────────── Section 2 ─────────────── */}
            <section className="rounded-2xl border border-rose-500/30 bg-zinc-900/80 p-5 sm:p-6" data-testid="voucher-section-2">
              <SectionHeader n={2} title="Eligibility & Assistance Verification" Icon={ClipboardCheck}
                subtitle="Select every program you're currently enrolled in · one is required" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {ASSISTANCE_PROGRAMS.map((p) => {
                  const on = form.assistance_programs.includes(p);
                  return (
                    <button
                      key={p} type="button"
                      onClick={() => toggleProgram(p)}
                      data-testid={`voucher-program-${p.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition text-left ${
                        on
                          ? "border-amber-400 bg-amber-500/20 text-amber-200"
                          : "border-white/15 bg-black/30 text-zinc-300 hover:border-amber-400/50"
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 rounded flex-shrink-0 border-2 ${
                        on ? "bg-amber-400 border-amber-400" : "border-zinc-500"
                      }`}>
                        {on && <CheckCircle2 className="h-3 w-3 text-black -mt-px -ml-px" />}
                      </span>
                      {p}
                    </button>
                  );
                })}
              </div>
              <label className="text-xs text-zinc-400 mb-2 block">
                Verification Document (state agency letter or benefit statement) *
              </label>
              <DocUploader
                kind="verification" maxMB={VERIF_MAX_MB}
                uploaded={verifDoc}
                onUploaded={setVerifDoc}
                onClear={() => setVerifDoc(null)}
                testid="voucher-verif-upload"
              />
            </section>

            {/* ─────────────── Section 3 ─────────────── */}
            <section className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-rose-500/5 p-5 sm:p-6" data-testid="voucher-section-3">
              <SectionHeader n={3} title="Program Acknowledgment & Submission" Icon={CheckCircle2} />
              <div className="rounded-lg border border-white/10 bg-black/40 p-4 mb-4 text-sm text-zinc-300 space-y-1.5">
                <div className="font-bold text-amber-300 uppercase tracking-wider text-xs mb-2">Key Program Rules</div>
                <div>· $10.00 per ride — rider pays any fare above $10.00</div>
                <div>· 3 vouchers per month per approved adult + 3 per eligible child</div>
                <div>· Book at least 24 hours in advance via dispatch <a className="text-amber-300 hover:underline" href={`tel:${(policy?.dispatch_phone || "").replace(/\D/g, "")}`}>{policy?.dispatch_phone || "(580) 219-4687"}</a> — or type <span className="font-black text-amber-300">&quot;human&quot;</span> into the live chat.</div>
                <div>· Physical embossed voucher required at pickup — child vouchers require the named child present</div>
                <div>· 2-hour cancellation window before scheduled pickup</div>
                <div>· Chamber Bucks accepted alongside standard payment</div>
                <div>· Non-transferable; duplication/alteration/resale = immediate disqualification</div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="voucher-ack-label">
                <input
                  type="checkbox"
                  checked={form.acknowledged}
                  onChange={(e) => setField("acknowledged", e.target.checked)}
                  data-testid="voucher-ack-checkbox"
                  className="h-5 w-5 mt-0.5 accent-amber-400 flex-shrink-0"
                />
                <span className="text-sm text-zinc-200">
                  I acknowledge that I have read, understood, and agree to the
                  Taxi Buller Services, LLC CCVAP Program (Community Care Voucher Assistance Program) policies.
                </span>
              </label>
              {/* v2.7.65+E165 · TCPA-style SMS consent — legal disclosure. */}
              <div className="mt-4">
                <SmsConsent
                  value={smsConsent}
                  onChange={setSmsConsent}
                  testIdPrefix="voucher-sms-consent"
                />
              </div>
              <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <Button
                  onClick={submit}
                  disabled={!canSubmit || submitting}
                  data-testid="voucher-submit"
                  className={`flex-1 font-black uppercase tracking-wider py-6 text-base ${
                    canSubmit
                      ? "bg-amber-500 hover:bg-amber-400 text-black"
                      : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                  }`}
                >
                  {submitting
                    ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Submitting…</>
                    : <><ShieldCheck className="h-5 w-5 mr-2" />Submit Application</>}
                </Button>
                <a
                  href="/docs/Community_Voucher_Program_Policy.pdf"
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs text-zinc-400 hover:text-amber-300 inline-flex items-center gap-1 justify-center"
                  data-testid="voucher-policy-inline-link"
                >
                  <FileText className="h-3.5 w-3.5" />Program Policy <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <p className="text-[11px] text-zinc-500 mt-3">
                A staff member will review your application within {policy?.review_hours || 72} hours.
              </p>
            </section>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
